import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
    ArrowLeft, ClipboardList, Plus, Pencil, Trash2, Loader2, Users, Clock,
    AlertTriangle, CheckCircle2, X, MapPin, UserPlus,
} from "lucide-react";
import { QuickAssignDialog } from "../components/QuickAssignDialog";
import { FrequencyPicker } from "../components/FrequencyPicker";
import { EmployeeMultiSelect } from "../components/EmployeeMultiSelect";
import { freqLabel, fmtH, isDateDriven, weekInterventions } from "../lib/planning";

const DAYS = [
    { v: 1, l: "Lun" }, { v: 2, l: "Mar" }, { v: 3, l: "Mer" }, { v: 4, l: "Gio" },
    { v: 5, l: "Ven" }, { v: 6, l: "Sab" }, { v: 7, l: "Dom" },
];
const dayLabels = (days) => DAYS.filter((d) => days?.includes(d.v)).map((d) => d.l).join(", ");
const PERIOD_LABELS = { settimana: "a settimana", mese: "al mese", intervento: "per intervento" };
const toMin = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return h * 60 + m; };
const shiftDur = (s) => { let d = toMin(s.end) - toMin(s.start); if (d <= 0) d += 24 * 60; return d / 60; };

export default function CantiereAttivitaPage() {
    const { wsId } = useParams();
    const [data, setData] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [editAct, setEditAct] = useState(null);
    const [assignCtx, setAssignCtx] = useState(null);
    const [showQuick, setShowQuick] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/presenze/worksites/${wsId}/activities`);
            setData(data);
        } catch {
            toast.error("Errore nel caricamento delle attività");
        }
    }, [wsId]);

    useEffect(() => { load(); }, [load]);

    const removeAct = async (act) => {
        if (!window.confirm(`Eliminare l'attività "${act.name}" e le sue assegnazioni?`)) return;
        try {
            await api.delete(`/presenze/activities/${act.id}`);
            toast.success("Attività eliminata");
            load();
        } catch { toast.error("Errore nell'eliminazione"); }
    };

    const removeAssignment = async (a) => {
        try {
            await api.delete(`/presenze/assignments/${a.id}`);
            toast.success("Assegnazione rimossa");
            load();
        } catch { toast.error("Errore nella rimozione"); }
    };

    if (!data) {
        return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>;
    }

    return (
        <div className="mx-auto max-w-5xl px-6 py-8" data-testid="attivita-page">
            <Link to="/presenze/cantieri" className="mb-4 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#376DB3]" data-testid="attivita-back-link">
                <ArrowLeft className="h-3.5 w-3.5" /> Tutti i cantieri
            </Link>
            <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                        <ClipboardList className="h-6 w-6" />
                    </div>
                    <div>
                        <h1 className="font-display text-2xl font-bold text-slate-800" data-testid="attivita-worksite-name">{data.worksite.name}</h1>
                        <p className="flex items-center gap-1 text-sm text-slate-500">
                            <MapPin className="h-3.5 w-3.5" /> {data.worksite.address || "—"} · attività, turni e monteore
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button variant="outline" onClick={() => setShowQuick(true)} className="gap-2" data-testid="attivita-quick-assign-btn">
                        <UserPlus className="h-4 w-4" /> Assegna dipendente
                    </Button>
                    <Button onClick={() => setShowCreate(true)} className="gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]" data-testid="attivita-add-btn">
                        <Plus className="h-4 w-4" /> Nuova attività
                    </Button>
                </div>
            </div>

            {data.activities.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-white p-12 text-center text-sm text-slate-500" data-testid="attivita-empty">
                    Nessuna attività configurata per questo cantiere. Crea la prima attività con i suoi turni e il monteore previsto.
                </div>
            ) : (
                data.activities.map((act) => (
                    <ActivityCard
                        key={act.id}
                        act={act}
                        onEdit={() => setEditAct(act)}
                        onDelete={() => removeAct(act)}
                        onAssign={(shift) => setAssignCtx({ activity: act, shift })}
                        onRemoveAssignment={removeAssignment}
                    />
                ))
            )}

            <ActivityDialog
                open={showCreate || !!editAct}
                activity={editAct}
                wsId={wsId}
                onClose={() => { setShowCreate(false); setEditAct(null); }}
                onSaved={() => { setShowCreate(false); setEditAct(null); load(); }}
            />
            <AssignDialog ctx={assignCtx} onClose={() => setAssignCtx(null)} onSaved={() => { setAssignCtx(null); load(); }} />
            <QuickAssignDialog open={showQuick} onClose={() => setShowQuick(false)} onSaved={() => { setShowQuick(false); load(); }} worksiteId={wsId} />
        </div>
    );
}

function CoverageBadge({ cov }) {
    if (cov.status === "carenza") {
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700" data-testid="coverage-badge">
                <AlertTriangle className="h-3.5 w-3.5" /> Mancano {fmtH(Math.abs(cov.diff))} h
            </span>
        );
    }
    if (cov.status === "eccesso") {
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-bold text-red-600" data-testid="coverage-badge">
                <AlertTriangle className="h-3.5 w-3.5" /> {fmtH(cov.diff)} h in più del previsto
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700" data-testid="coverage-badge">
            <CheckCircle2 className="h-3.5 w-3.5" /> Copertura completa
        </span>
    );
}

function ActivityCard({ act, onEdit, onDelete, onAssign, onRemoveAssignment }) {
    const cov = act.coverage;
    const pct = cov.target_hours > 0 ? Math.min(100, (cov.assigned_hours / cov.target_hours) * 100) : 0;
    const barColor = cov.status === "ok" ? "bg-emerald-500" : cov.status === "carenza" ? "bg-amber-400" : "bg-red-500";

    return (
        <div className="mb-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid={`attivita-card-${act.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <div className="flex items-center gap-2">
                        <h2 className="font-display text-lg font-bold text-slate-800">{act.name}</h2>
                        {act.active === false && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">NON ATTIVA</span>}
                    </div>
                    <p className="text-xs text-slate-500">
                        Monteore previsto: <b className="font-mono-numbers">{fmtH(act.monteore_hours)} h</b> {PERIOD_LABELS[act.monteore_period]}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <CoverageBadge cov={cov} />
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onEdit} title="Modifica" data-testid={`attivita-edit-${act.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={onDelete} title="Elimina" data-testid={`attivita-delete-${act.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                </div>
            </div>

            <div className="mt-3">
                <div className="flex items-center justify-between text-xs text-slate-500">
                    <span data-testid={`attivita-coverage-text-${act.id}`}>
                        Assegnate <b className="font-mono-numbers text-slate-700">{fmtH(cov.assigned_hours)} h</b> su <b className="font-mono-numbers text-slate-700">{fmtH(cov.target_hours)} h</b> {PERIOD_LABELS[act.monteore_period]}
                    </span>
                    <span className="font-mono-numbers">{Math.round(pct)}%</span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-100">
                    <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
                </div>
            </div>

            {act.shifts.map((s) => {
                const shiftAssigns = act.assignments.filter((a) => a.shift_id === s.id);
                return (
                    <div key={s.id} className="mt-3 rounded-md border border-slate-100 bg-slate-50/60 p-3" data-testid={`turno-${s.id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                                <Clock className="h-4 w-4 text-[#376DB3]" />
                                {isDateDriven(s.frequency) ? "Secondo date" : dayLabels(s.days)} · <span className="font-mono-numbers">{s.start}–{s.end}</span>
                                <span className="text-xs font-normal text-slate-400">({fmtH(shiftDur(s))} h)</span>
                                {s.frequency?.type && s.frequency.type !== "weekly" && (
                                    <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-bold text-violet-700" data-testid={`turno-freq-${s.id}`}>{freqLabel(s.frequency)}</span>
                                )}
                            </span>
                            <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs" onClick={() => onAssign(s)} data-testid={`turno-assign-btn-${s.id}`}>
                                <Users className="h-3.5 w-3.5" /> Assegna
                            </Button>
                        </div>
                        {shiftAssigns.length === 0 ? (
                            <div className="mt-2 text-xs font-medium text-amber-600" data-testid={`turno-empty-${s.id}`}>Nessun dipendente assegnato a questo turno</div>
                        ) : (
                            <ul className="mt-2 divide-y divide-slate-100">
                                {shiftAssigns.map((a) => (
                                    <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5 text-sm" data-testid={`assegnazione-${a.id}`}>
                                        <Link to={`/dipendenti/${a.employee_id}`} className="font-medium text-slate-700 hover:text-[#376DB3] hover:underline">
                                            {a.employee_name}
                                            {a.matricola && <span className="ml-1.5 text-[11px] font-normal text-slate-400">matr. {a.matricola}</span>}
                                        </Link>
                                        <span className="flex items-center gap-3">
                                            <span className="text-xs text-slate-500">{a.days?.length ? dayLabels(a.days) : "tutte le occorrenze"}</span>
                                            <span className="font-mono-numbers text-xs font-semibold text-slate-700" title="Media settimanale (tiene conto della frequenza)">{fmtH(a.hours_week)} h/sett</span>
                                            <button type="button" onClick={() => onRemoveAssignment(a)} className="text-slate-300 hover:text-red-500" title="Rimuovi" data-testid={`assegnazione-del-${a.id}`}>
                                                <X className="h-4 w-4" />
                                            </button>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function DayPills({ days, allowed, onToggle, testPrefix }) {
    return (
        <div className="flex flex-wrap gap-1">
            {DAYS.map((d) => {
                const enabled = !allowed || allowed.includes(d.v);
                const on = days.includes(d.v);
                return (
                    <button
                        key={d.v}
                        type="button"
                        disabled={!enabled}
                        onClick={() => onToggle(d.v)}
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${on ? "bg-[#376DB3] text-white" : enabled ? "bg-slate-100 text-slate-600 hover:bg-slate-200" : "bg-slate-50 text-slate-300"}`}
                        data-testid={`${testPrefix}-${d.v}`}
                    >
                        {d.l}
                    </button>
                );
            })}
        </div>
    );
}

function ActivityDialog({ open, activity, wsId, onClose, onSaved }) {
    const [name, setName] = useState("");
    const [monteore, setMonteore] = useState("");
    const [period, setPeriod] = useState("settimana");
    const [shifts, setShifts] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setName(activity?.name || "");
            setMonteore(activity?.monteore_hours != null ? String(activity.monteore_hours) : "");
            setPeriod(activity?.monteore_period || "settimana");
            setShifts(activity?.shifts?.map((s) => ({ ...s, days: [...s.days] })) || [{ days: [1, 2, 3, 4, 5], start: "06:00", end: "09:00" }]);
        }
    }, [open, activity]);

    const updShift = (i, patch) => setShifts((p) => p.map((s, j) => (j === i ? { ...s, ...patch } : s)));
    const toggleDay = (i, d) => {
        const cur = shifts[i].days;
        updShift(i, { days: cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort((a, b) => a - b) });
    };

    const save = async () => {
        const mh = parseFloat(String(monteore).replace(",", "."));
        if (!name.trim()) { toast.error("Inserisci il nome dell'attività"); return; }
        if (Number.isNaN(mh) || mh <= 0) { toast.error("Inserisci il monteore previsto"); return; }
        if (shifts.length === 0) { toast.error("Aggiungi almeno un turno"); return; }
        if (shifts.some((s) => !isDateDriven(s.frequency) && !s.days?.length)) { toast.error("Seleziona i giorni per ogni turno"); return; }
        setSaving(true);
        try {
            const payload = {
                name: name.trim(),
                monteore_hours: mh,
                monteore_period: period,
                shifts,
                active: activity ? activity.active !== false : true,
            };
            if (activity) await api.patch(`/presenze/activities/${activity.id}`, payload);
            else await api.post(`/presenze/worksites/${wsId}/activities`, payload);
            toast.success(activity ? "Attività aggiornata" : "Attività creata");
            onSaved();
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Errore nel salvataggio");
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] max-w-2xl overflow-y-auto" data-testid="attivita-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-slate-800">{activity ? "Modifica attività" : "Nuova attività"}</DialogTitle>
                    <DialogDescription>Definisci turni, giorni di intervento e monteore previsto: le assegnazioni verranno confrontate col monteore.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Nome attività *</Label>
                            <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Pulizia camere" data-testid="attivita-name-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Monteore *</Label>
                                <Input className="mt-1" type="number" min={0.01} step={0.01} value={monteore} onChange={(e) => setMonteore(e.target.value)} placeholder="30,00" data-testid="attivita-monteore-input" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Periodo</Label>
                                <select value={period} onChange={(e) => setPeriod(e.target.value)}
                                    className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="attivita-period-select">
                                    <option value="settimana">ore a settimana</option>
                                    <option value="mese">ore al mese</option>
                                    <option value="intervento">ore per intervento</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Turni (fasce orarie e giorni di intervento) *</Label>
                        <div className="mt-2 space-y-2">
                            {shifts.map((s, i) => (
                                <div key={s.id || i} className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50/60 p-2.5" data-testid={`shift-editor-${i}`}>
                                    {isDateDriven(s.frequency) ? (
                                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-500" data-testid={`turno-days-by-date-${i}`}>Giorni decisi dalle date</span>
                                    ) : (
                                        <DayPills days={s.days} onToggle={(d) => toggleDay(i, d)} testPrefix={`turno-day-${i}`} />
                                    )}
                                    <div className="flex items-center gap-1.5">
                                        <input type="time" value={s.start} onChange={(e) => updShift(i, { start: e.target.value })}
                                            className="h-8 rounded-md border border-slate-200 px-1.5 text-xs" data-testid={`turno-start-${i}`} />
                                        <span className="text-xs text-slate-400">→</span>
                                        <input type="time" value={s.end} onChange={(e) => updShift(i, { end: e.target.value })}
                                            className="h-8 rounded-md border border-slate-200 px-1.5 text-xs" data-testid={`turno-end-${i}`} />
                                        <span className="font-mono-numbers text-[11px] text-slate-400">({fmtH(shiftDur(s))} h)</span>
                                    </div>
                                    <FrequencyPicker compact value={s.frequency || { type: "weekly" }} onChange={(f) => updShift(i, { frequency: f })} testPrefix={`turno-freq-${i}`} />
                                    {shifts.length > 1 && (
                                        <button type="button" onClick={() => setShifts((p) => p.filter((_, j) => j !== i))}
                                            className="ml-auto text-slate-300 hover:text-red-500" data-testid={`turno-remove-${i}`}>
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        <Button type="button" variant="outline" size="sm" className="mt-2 gap-1.5 text-xs"
                            onClick={() => setShifts((p) => [...p, { days: [1, 2, 3, 4, 5], start: "06:00", end: "09:00" }])}
                            data-testid="attivita-add-shift-btn">
                            <Plus className="h-3.5 w-3.5" /> Aggiungi turno
                        </Button>
                    </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={onClose}>Annulla</Button>
                    <Button onClick={save} disabled={saving} className="gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]" data-testid="attivita-save-btn">
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        Salva
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function AssignDialog({ ctx, onClose, onSaved }) {
    const shift = ctx?.shift;
    const [employees, setEmployees] = useState(null);
    const [sel, setSel] = useState({}); // employee_id → giorni
    const [saving, setSaving] = useState(false);
    const byDate = isDateDriven(shift?.frequency);

    useEffect(() => {
        if (ctx) {
            setSel({});
            api.get("/presenze/planning/employees")
                .then((r) => setEmployees(r.data))
                .catch(() => toast.error("Errore nel caricamento dei dipendenti"));
        }
    }, [ctx]);

    const ids = Object.keys(sel);
    const setIds = (next) => setSel((p) => Object.fromEntries(next.map((id) => [id, p[id] || [...(shift?.days || [])]])));
    const toggleDay = (id, d) => setSel((p) => ({ ...p, [id]: p[id].includes(d) ? p[id].filter((x) => x !== d) : [...p[id], d].sort((a, b) => a - b) }));
    const hoursFor = (days) => (shift ? shiftDur(shift) * weekInterventions(shift.frequency, days) : 0);
    const rows = ids.map((id) => {
        const emp = employees?.find((e) => e.id === id);
        const hours = hoursFor(sel[id]);
        return { id, emp, days: sel[id], hours, over: !!(emp?.contratto_ore && emp.assigned_week_hours + hours > emp.contratto_ore) };
    });

    const save = async () => {
        if (!ids.length) { toast.error("Seleziona almeno un dipendente"); return; }
        const noDays = rows.find((r) => !byDate && !r.days.length);
        if (noDays) { toast.error(`Seleziona almeno un giorno per ${noDays.emp?.cognome || "ogni dipendente"}`); return; }
        setSaving(true);
        try {
            await api.post(`/presenze/activities/${ctx.activity.id}/assignments/bulk`, {
                shift_id: shift.id, items: rows.map((r) => ({ employee_id: r.id, days: byDate ? [] : r.days })),
            });
            toast.success(ids.length === 1 ? "Dipendente assegnato al turno" : `${ids.length} dipendenti assegnati al turno`);
            onSaved();
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Errore nel salvataggio");
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={!!ctx} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] max-w-2xl overflow-y-auto" data-testid="assegna-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-slate-800">Assegna dipendenti — {ctx?.activity.name}</DialogTitle>
                    <DialogDescription>
                        Turno <span className="font-mono-numbers">{shift?.start}–{shift?.end}</span> · {byDate ? freqLabel(shift?.frequency) : dayLabels(shift?.days)}
                        {" "}· seleziona una o più persone{byDate ? "" : " e per ciascuna i giorni"}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Dipendenti *</Label>
                        <div className="mt-1">
                            {employees ? (
                                <EmployeeMultiSelect employees={employees} selected={ids} onChange={setIds} testPrefix="assegna-emp" maxHeight="max-h-44" />
                            ) : (
                                <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-slate-300" /></div>
                            )}
                        </div>
                    </div>
                    {rows.length > 0 && (
                        <div className="space-y-2" data-testid="assegna-rows">
                            {rows.map((r) => (
                                <div key={r.id} className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2" data-testid={`assegna-row-${r.id}`}>
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div className="text-sm font-semibold text-slate-700">
                                            {r.emp?.cognome} {r.emp?.nome}
                                            {r.emp?.matricola && <span className="ml-1 text-xs font-normal text-slate-400">matr. {r.emp.matricola}</span>}
                                        </div>
                                        <div className="font-mono-numbers text-xs text-slate-600" data-testid={`assegna-hours-${r.id}`}>
                                            <b>{fmtH(r.hours)} h/sett</b>{byDate ? " in media" : ""} · assegnate {fmtH(r.emp?.assigned_week_hours)}h{r.emp?.contratto_ore ? ` / ${fmtH(r.emp.contratto_ore)}h` : ""}
                                        </div>
                                    </div>
                                    {byDate ? (
                                        <div className="mt-1 text-xs text-slate-500">Assegnato a tutte le occorrenze ({freqLabel(shift?.frequency)}).</div>
                                    ) : (
                                        <div className="mt-1.5">
                                            <DayPills days={r.days} allowed={shift?.days} onToggle={(d) => toggleDay(r.id, d)} testPrefix={`assegna-day-${r.id}`} />
                                        </div>
                                    )}
                                    {r.over && (
                                        <div className="mt-1.5 flex items-start gap-1.5 text-xs font-medium text-amber-700" data-testid={`assegna-over-contract-${r.id}`}>
                                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                            Supera le ore contrattuali settimanali ({fmtH(r.emp.assigned_week_hours + r.hours)} h su {fmtH(r.emp.contratto_ore)} h).
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    {rows.length === 0 && (
                        <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="assegna-hours-preview">
                            Nessun dipendente selezionato. {byDate ? "Ogni persona selezionata sarà assegnata a tutte le occorrenze." : `Per ogni persona potrai scegliere i giorni (predefiniti: ${dayLabels(shift?.days)}).`}
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={onClose}>Annulla</Button>
                    <Button onClick={save} disabled={saving || !employees} className="gap-2 bg-[#376DB3] text-white hover:bg-[#2c5a96]" data-testid="assegna-save-btn">
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        Assegna{ids.length > 1 ? ` (${ids.length})` : ""}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
